import type {BfPools} from '@/pages/components/tax-computation/sub/DepreciationScheduleTable';
import type {
    AuditTaxComBO,
    AuditTaxComExtraItemBO,
    AuditTaxComPropertyBO,
    TaxBasicProfitsVO,
    TaxCommercialBuilding,
    TaxComputationResult,
    TaxCostPerBook,
    TaxDepreciationAllowanceScheduleVO,
    TaxExtraItem,
} from '@/services/auditService/interface/tax-com';
import {
    computeCommercialAllowanceFields,
    type CommercialBuildRow,
} from '@/pages/components/tax-computation/sub/CommercialBuildScheduleTable';

export function poolsEqual(a?: BfPools | null, b?: BfPools | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.tenPool === b.tenPool
        && a.twentyPool === b.twentyPool
        && a.thirtyPool === b.thirtyPool
        && a.hpThirtyPool === b.hpThirtyPool;
}

const ZERO_BF_POOLS: BfPools = {tenPool: 0, twentyPool: 0, thirtyPool: 0, hpThirtyPool: 0};

/** Read four-pool amounts from a balancingList / schedule row by row name. */
export function poolsFromBalancingRowName(
    rows: Array<{rowName?: string | null; item?: string | null; tenPool?: number | null; twentyPool?: number | null; thirtyPool?: number | null; hpThirtyPool?: number | null}> | null | undefined,
    rowName: string,
): BfPools {
    const key = (rowName || '').trim();
    const row = (rows || []).find((r) => ((r?.rowName || r?.item || '') as string).trim() === key) || null;
    if (!row) return {...ZERO_BF_POOLS};
    return {
        tenPool: Number(row.tenPool) || 0,
        twentyPool: Number(row.twentyPool) || 0,
        thirtyPool: Number(row.thirtyPool) || 0,
        hpThirtyPool: Number(row.hpThirtyPool) || 0,
    };
}

/**
 * Same formula as DepreciationScheduleTable W.D.V. c/f
 * (total + A.A. + Balancing charge pools + Balancing allowance).
 */
export function computeDepreciationWdvCfPools(args: {
    wdvBf: BfPools;
    ba: BfPools;
    salesProceed: BfPools;
    additions: BfPools;
    ia60: BfPools;
}): BfPools {
    const roundHalfUp = (value: number) => {
        if (!Number.isFinite(value) || value === 0) return 0;
        return value >= 0 ? Math.round(value) : -Math.round(Math.abs(value));
    };
    const total: BfPools = {
        tenPool: args.wdvBf.tenPool + args.additions.tenPool + args.ia60.tenPool + args.salesProceed.tenPool,
        twentyPool: args.wdvBf.twentyPool + args.additions.twentyPool + args.ia60.twentyPool + args.salesProceed.twentyPool,
        thirtyPool: args.wdvBf.thirtyPool + args.additions.thirtyPool + args.ia60.thirtyPool + args.salesProceed.thirtyPool,
        hpThirtyPool: args.wdvBf.hpThirtyPool + args.additions.hpThirtyPool + args.ia60.hpThirtyPool + args.salesProceed.hpThirtyPool,
    };
    const aaRaw = {
        tenPool: total.tenPool < 0 ? 0 : -(total.tenPool * 0.1),
        twentyPool: total.twentyPool < 0 ? 0 : -(total.twentyPool * 0.2),
        thirtyPool: total.thirtyPool < 0 ? 0 : -(total.thirtyPool * 0.3),
        hpThirtyPool: total.hpThirtyPool < 0 ? 0 : -(total.hpThirtyPool * 0.3),
    };
    const aa: BfPools = {
        tenPool: roundHalfUp(aaRaw.tenPool),
        twentyPool: roundHalfUp(aaRaw.twentyPool),
        thirtyPool: roundHalfUp(aaRaw.thirtyPool),
        hpThirtyPool: roundHalfUp(aaRaw.hpThirtyPool),
    };
    const bc: BfPools = {
        tenPool: total.tenPool <= 0 ? Math.abs(total.tenPool) : 0,
        twentyPool: total.twentyPool <= 0 ? Math.abs(total.twentyPool) : 0,
        thirtyPool: total.thirtyPool <= 0 ? Math.abs(total.thirtyPool) : 0,
        hpThirtyPool: total.hpThirtyPool <= 0 ? Math.abs(total.hpThirtyPool) : 0,
    };
    return {
        tenPool: total.tenPool + aa.tenPool + bc.tenPool + args.ba.tenPool,
        twentyPool: total.twentyPool + aa.twentyPool + bc.twentyPool + args.ba.twentyPool,
        thirtyPool: total.thirtyPool + aa.thirtyPool + bc.thirtyPool + args.ba.thirtyPool,
        hpThirtyPool: total.hpThirtyPool + aa.hpThirtyPool + bc.hpThirtyPool + args.ba.hpThirtyPool,
    };
}

export function normalizePropertyList(list: AuditTaxComPropertyBO[] | undefined): AuditTaxComPropertyBO[] {
    return (list || [])
        .map((p) => ({
            id: p.id ?? undefined,
            annualId: p.annualId ?? undefined,
            ppeId: p.ppeId ?? undefined,
            propertyType: p.propertyType ?? undefined,
            stampDuty: Number(p.stampDuty) || 0,
            clFee: Number(p.clFee) || 0,
        }))
        .sort((a, b) => {
            const ak = `${a.ppeId ?? ''}:${a.propertyType ?? ''}`;
            const bk = `${b.ppeId ?? ''}:${b.propertyType ?? ''}`;
            return ak.localeCompare(bk);
        });
}

export function propertyListKey(list: AuditTaxComPropertyBO[] | undefined): string {
    return JSON.stringify(normalizePropertyList(list));
}

export function buildTaxComPayloadKey(out: AuditTaxComBO): string {
    const numOrUndef = (v: number | undefined | null): number | undefined =>
        v == null ? undefined : Number(v) || 0;
    const normalized: AuditTaxComBO = {
        ...out,
        companyId: Number(out.companyId) || 0,
        annualId: Number(out.annualId) || 0,
        oneReducingBf: numOrUndef(out.oneReducingBf),
        wdvBf: numOrUndef(out.wdvBf),
        wdvBfTenPool: numOrUndef(out.wdvBfTenPool),
        wdvBfTwentyPool: numOrUndef(out.wdvBfTwentyPool),
        wdvBfThirtyPool: numOrUndef(out.wdvBfThirtyPool),
        wdvBfHpThirtyPool: numOrUndef(out.wdvBfHpThirtyPool),
        baTenPool: numOrUndef(out.baTenPool),
        baTwentyPool: numOrUndef(out.baTwentyPool),
        baThirtyPool: numOrUndef(out.baThirtyPool),
        baHpThirtyPool: numOrUndef(out.baHpThirtyPool),
        taxPayment: Number(out.taxPayment) || 0,
        taxLossBf: Number(out.taxLossBf) || 0,
        commercialFullyUsedPriorYears: numOrUndef(out.commercialFullyUsedPriorYears),
        leaseholdFullyUsedPriorYears: numOrUndef(out.leaseholdFullyUsedPriorYears),
        p1Note: out.p1Note != null ? out.p1Note : undefined,
        p1Confirm: out.p1Confirm != null ? out.p1Confirm : undefined,
        commercialPriorLabel: out.commercialPriorLabel != null ? out.commercialPriorLabel : null,
        commercialPriorValue: out.commercialPriorValue != null ? Number(out.commercialPriorValue) : null,
        leaseholdPriorLabel: out.leaseholdPriorLabel != null ? out.leaseholdPriorLabel : null,
        leaseholdPriorValue: out.leaseholdPriorValue != null ? Number(out.leaseholdPriorValue) : null,
        properties: normalizePropertyList(out.properties),
        extraAddItems: normalizeExtraItemList(out.extraAddItems),
        extraLessItems: normalizeExtraItemList(out.extraLessItems),
    };
    return JSON.stringify(normalized);
}

export function normalizeExtraItemList(
    list: AuditTaxComBO['extraAddItems'] | undefined | null,
): AuditTaxComExtraItemBO[] | null {
    if (list == null) return null;
    return (list || [])
        .map((item, idx) => ({
            id: item?.id ?? undefined,
            section: item?.section,
            itemName: (item?.itemName ?? '').trim(),
            amount: Number(item?.amount) || 0,
            sortNo: item?.sortNo ?? idx,
        }))
        .filter((item) => !!item.itemName)
        .sort((a, b) => {
            const sk = `${a.sortNo ?? 0}:${(a.itemName ?? '').toLowerCase()}`;
            const bk = `${b.sortNo ?? 0}:${(b.itemName ?? '').toLowerCase()}`;
            return sk.localeCompare(bk);
        });
}

/** Recompute lessTotal from fixed rows + schedule add-backs + user extras (whole dollars). */
export function recomputeLessTotalWithExtras(
    data: TaxComputationResult | TaxBasicProfitsVO | null | undefined,
    extraAddItems?: TaxExtraItem[] | null,
    extraLessItems?: TaxExtraItem[] | null,
): number {
    if (!data) return 0;
    const n = (v?: number | null) => Number(v) || 0;
    let total = 0;
    total += n(data.perIncomeStatement);
    total += n(data.attachedHereWith);
    total += n(data.interestExpense);
    total += n(data.penaltiesFinesStampDuties);
    total += n(data.donationCharitable);
    total += n(data.companySetUpFee);
    total += n(data.depreciationExpenses);
    total += n(data.lossDisposalOfMachineryPlant);
    total += n(data.provisionDoubtful);
    total += n(data.exchangeLoss);
    total += n(data.balancingCharge);
    total += n(data.withholdingTax);
    for (const item of data.scheduleAddBacks ?? []) {
        total += n(item?.addBackAmount);
    }
    for (const item of extraAddItems ?? data.extraAddItems ?? []) {
        total += n(item?.amount);
    }
    total += n(data.netInterestIncome);
    total += n(data.subsidiesFromGovernment);
    total += n(data.purchaseCostHardSoftware);
    total += n(data.gainDisposalOfFixedAssets);
    total += n(data.capitalExpenditure);
    total += n(data.depreciation);
    total += n(data.dividendIncome);
    total += n(data.industrialBuilding);
    total += n(data.commercialBuilding);
    total += n(data.exchangeGain);
    total += n(data.balancingAllowance);
    total += n(data.excessWithholdingTax);
    for (const item of extraLessItems ?? data.extraLessItems ?? []) {
        total += n(item?.amount);
    }
    return total;
}

/**
 * Optimistic Type A/B/C fields driven by lessTotal (mirrors backend P1a/P1b/P1c basics).
 * Type C tax-payable lines still need save→refresh; assessment / donation / loss c/f update here.
 */
export function patchProfitsTaxFromLessTotal(
    data: TaxComputationResult,
    lessTotal: number,
): TaxComputationResult {
    const lt = Number(lessTotal) || 0;
    const donationCharitable = Math.abs(Number(data.donationCharitable) || 0);
    const donationCap = lt > 0 ? lt * 0.35 : 0;
    const donationMadeValue = Math.round(-Math.min(donationCap, donationCharitable));
    const assessmentValue = Math.round(lt + donationMadeValue);

    const next: TaxComputationResult = {...data, lessTotal: lt};

    if (data.profitsTaxA) {
        const addValue = -lt;
        const assessmentA = Math.round(addValue + lt);
        const taxLossBfA = Number(data.profitsTaxA.taxLossBf) || 0;
        const prevAdd = Number(data.profitsTaxA.addValue) || 0;
        const prevAfter = Number(data.profitsTaxA.afterRateValue) || 0;
        const afterRateValue = prevAdd !== 0 && (data.profitsTaxA.addName || '').includes('Offshore')
            ? Math.round(addValue * (prevAfter / prevAdd))
            : data.profitsTaxA.afterRateValue;
        next.profitsTaxA = {
            ...data.profitsTaxA,
            addValue,
            assessmentValue: assessmentA,
            adjustedAssessment: assessmentA,
            taxLossCf: Math.round(assessmentA + taxLossBfA),
            afterRateValue,
        };
    }

    if (data.profitsTaxB) {
        const prevAssess = Number(data.profitsTaxB.assessmentValue) || 0;
        const prevAdj = Number(data.profitsTaxB.adjustedAssessment) || 0;
        const rate = prevAssess !== 0 ? prevAdj / prevAssess : 1;
        const adjustedAssessment = Math.round(assessmentValue * rate);
        const prevCf = Number(data.profitsTaxB.taxLossCf) || 0;
        const bfPart = prevCf - prevAdj;
        next.profitsTaxB = {
            ...data.profitsTaxB,
            donationMadeValue,
            assessmentValue,
            adjustedAssessment,
            taxLossCf: Math.round(bfPart + adjustedAssessment),
        };
    }

    if (data.profitsTaxC) {
        const prevAssess = Number(data.profitsTaxC.assessmentValue) || 0;
        const prevHkd = Number(data.profitsTaxC.assessableValue) || 0;
        const rate = prevAssess !== 0 ? prevHkd / prevAssess : 1;
        const assessableValue = Math.round(assessmentValue * rate);
        const prevCf = Number(data.profitsTaxC.taxLossCf) || 0;
        const taxLossBfC = Number(data.profitsTaxC.taxLossBf) || 0;
        // Prefer preserving prior (cf - assessmentHKD) gap when assessableValue was the HKD assessment base
        const bfPart = prevCf - prevHkd;
        next.profitsTaxC = {
            ...data.profitsTaxC,
            donationMadeValue,
            assessmentValue,
            assessableValue: prevHkd !== 0 || rate !== 1 ? assessableValue : data.profitsTaxC.assessableValue,
            taxLossCf: Math.round(
                (prevHkd !== 0 || rate !== 1 ? bfPart + assessableValue : taxLossBfC + assessmentValue),
            ),
            assessableProfit: assessableValue !== 0 ? assessableValue : assessmentValue,
        };
    }

    return next;
}

export function toExtraItemSaveList(
    list: TaxExtraItem[] | undefined | null,
    section: 'ADD' | 'LESS',
): AuditTaxComExtraItemBO[] {
    return (list || [])
        .map((item, idx) => ({
            id: item.id,
            section,
            itemName: (item.itemName ?? '').trim(),
            amount: Number(item.amount) || 0,
            sortNo: item.sortNo ?? idx,
        }))
        .filter((item) => !!item.itemName);
}

/**
 * After save/refresh, attach server ids onto local extra rows without replacing
 * in-progress names/amounts or remounting via clientKey.
 */
export function mergeHydratedExtraItems(
    local: TaxExtraItem[] | undefined | null,
    server: TaxExtraItem[] | null | undefined,
): TaxExtraItem[] {
    const current = local ?? [];
    const remote = server ?? [];
    const used = new Set<number>();
    const take = (hit: TaxExtraItem | undefined): TaxExtraItem | undefined => {
        if (!hit) return undefined;
        const idx = remote.indexOf(hit);
        if (idx >= 0) {
            if (used.has(idx)) return undefined;
            used.add(idx);
        }
        return hit;
    };
    return current.map((row) => {
        let hit: TaxExtraItem | undefined;
        if (row.id != null) {
            hit = take(remote.find((item, i) => !used.has(i) && item.id === row.id));
        }
        if (!hit && (row.itemName ?? '').trim()) {
            const name = (row.itemName ?? '').trim();
            hit = take(remote.find((item, i) => !used.has(i) && (item.itemName ?? '').trim() === name));
        }
        if (!hit) return row;
        return {
            ...row,
            id: hit.id ?? row.id,
        };
    });
}

export function mergePropertyStampEdits(
    server: AuditTaxComPropertyBO[],
    preferred: AuditTaxComPropertyBO[],
): AuditTaxComPropertyBO[] {
    if (!preferred?.length) return server;

    return server.map((serverRow, index) => {
        const preferredRow = preferred.find(
            (row) => row.ppeId != null
                && row.ppeId === serverRow.ppeId
                && row.propertyType === serverRow.propertyType,
        ) ?? preferred[index];
        if (!preferredRow) return serverRow;

        const serverStamp = Number(serverRow.stampDuty) || 0;
        const serverFee = Number(serverRow.clFee) || 0;
        const preferredStamp = Number(preferredRow.stampDuty) || 0;
        const preferredFee = Number(preferredRow.clFee) || 0;

        if (serverStamp === preferredStamp && serverFee === preferredFee) return serverRow;
        return {...serverRow, stampDuty: preferredStamp, clFee: preferredFee};
    });
}

/** 将刚保存的 property 快照写回 section 行（refresh 命中旧缓存时保持 UI 与 DB 一致） */
export function applySavedPropertiesToCommercialSection<T extends {
    priorLocationsProperties?: Array<{ ppeId?: number; stampDuty: number; commisionLegalFees: number }>;
    yearLocationsProperties?: Array<{ ppeId?: number; stampDuty: number; commisionLegalFees: number }>;
}>(
    section: T,
    saved: AuditTaxComPropertyBO[],
    options?: {strictPpeMatch?: boolean},
): T {
    if (!saved?.length) return section;
    const strictPpeMatch = !!options?.strictPpeMatch;

    const patchRows = (
        rows: Array<{ ppeId?: number; stampDuty: number; commisionLegalFees: number }>,
        propertyType: number,
    ) => rows.map((row, index) => {
        const savedRow = saved.find(
            (p) => p.ppeId != null && p.ppeId === row.ppeId && p.propertyType === propertyType,
        ) ?? (strictPpeMatch ? undefined : saved[index]);
        if (!savedRow) return row;
        return {
            ...row,
            stampDuty: Number(savedRow.stampDuty) || 0,
            commisionLegalFees: Number(savedRow.clFee) || 0,
        };
    });

    return {
        ...section,
        priorLocationsProperties: patchRows(section.priorLocationsProperties || [], 0),
        yearLocationsProperties: patchRows(section.yearLocationsProperties || [], 1),
    };
}

function hasCommercialDisposalFields(building?: TaxCommercialBuilding | null): boolean {
    if (!building) return false;
    return Math.abs(Number(building.salesProceed) || 0) > 0
        || Math.abs(Number(building.balancingCharge) || 0) > 0
        || building.subtotalAfterSales != null;
}

function mapCostPerBookToRows(list?: TaxCostPerBook[] | null): CommercialBuildRow[] {
    return (list || []).map((r) => ({
        date: r?.date || null,
        item: r?.equipmentName || '',
        costPerBook: Number(r?.costPerBook) || 0,
        stampDuty: Number(r?.stampDuty) || 0,
        commisionLegalFees: Number(r?.commissionLegalFee) || 0,
        ppeId: r?.ppeId != null ? Number(r.ppeId) : undefined,
        propertyId: r?.propertyId != null ? Number(r.propertyId) : undefined,
    }));
}

function annualAllowanceForBuilding(
    building: TaxCommercialBuilding | null | undefined,
    saved: AuditTaxComPropertyBO[],
    bf: number,
    leasehold: boolean,
    fullyUsedOverride?: number,
    priorValueOverride?: number | null,
): number {
    if (!building) return 0;
    const base = {
        priorLocationsProperties: mapCostPerBookToRows(building.priorLocationsProperties),
        yearLocationsProperties: mapCostPerBookToRows(building.yearLocationsProperties),
        fullyUsedInPriorYears: fullyUsedOverride != null
            ? Number(fullyUsedOverride) || 0
            : Number(building.fullyUsedInPriorYears) || 0,
    };
    // strict：commercial / leasehold 共用 propertyType 0/1，禁止 index 回退以免串行
    const patched = applySavedPropertiesToCommercialSection(base, saved, {strictPpeMatch: true});
    const effectivePriorOverride = priorValueOverride != null
        ? priorValueOverride
        : (leasehold
            ? (building.leaseholdPriorValue != null ? Number(building.leaseholdPriorValue) : null)
            : (building.commercialPriorValue != null ? Number(building.commercialPriorValue) : null));
    return Number(computeCommercialAllowanceFields(patched, bf, leasehold, effectivePriorOverride).annualAllowance) || 0;
}

export type P1CommercialBuildingLinkInput = {
    p11: TaxDepreciationAllowanceScheduleVO | null | undefined;
    /** Stamp duty + Commission and legal fees（clFee） */
    properties: AuditTaxComPropertyBO[] | undefined;
    /** Commercial「Reducing value b/f」 */
    oneReducingBf: number;
    /** Leasehold「W.D.V b/f」/ Reducing value b/f */
    wdvBf: number;
    commercialFullyUsedPriorYears?: number;
    leaseholdFullyUsedPriorYears?: number;
    /** Commercial prior-years value override (HK$/2); when set, used in 4% formula */
    commercialPriorValue?: number | null;
    /** Leasehold prior-years value override (full scale); when set, used in 4% formula */
    leaseholdPriorValue?: number | null;
};

/**
 * Stamp duty / Commission and legal fees / Reducing value b/f（及 leasehold WDV b/f）
 * 均进入与 P1.1 相同的 4% 公式，推算 P1 Commercial building。
 * 存在 RX12 disposal 字段时返回 null（沿用后端，避免覆盖 balancing charge 路径）。
 */
export function resolveP1CommercialBuildingFromSavedProperties(
    args: P1CommercialBuildingLinkInput,
): number | null {
    const {p11, properties} = args;
    if (!p11) return null;
    if (hasCommercialDisposalFields(p11.commercialBuilding)
        || hasCommercialDisposalFields(p11.thisYearCommercialBuilding)) {
        return null;
    }

    const saved = normalizePropertyList(properties);
    const commercialAa = annualAllowanceForBuilding(
        p11.commercialBuilding,
        saved,
        Number(args.oneReducingBf) || 0,
        false,
        args.commercialFullyUsedPriorYears,
        args.commercialPriorValue,
    );
    const leaseholdAa = annualAllowanceForBuilding(
        p11.thisYearCommercialBuilding,
        saved,
        Number(args.wdvBf) || 0,
        true,
        args.leaseholdFullyUsedPriorYears,
        args.leaseholdPriorValue,
    );
    return commercialAa + leaseholdAa;
}

/** 将 P1 Commercial building 与 lessTotal 联动为与 P1.1 4% 一致（无感，不切 tab） */
export function patchTaxMainCommercialBuilding(
    main: TaxComputationResult | null | undefined,
    nextCommercialBuilding: number,
): TaxComputationResult | null {
    if (!main) return null;
    const prev = Number(main.commercialBuilding) || 0;
    const next = Number(nextCommercialBuilding) || 0;
    if (prev === next) return main;
    return {
        ...main,
        commercialBuilding: next,
        lessTotal: (Number(main.lessTotal) || 0) + (next - prev),
    };
}

/** 同步 P1.1 store 中的 annualAllowance，避免与本地 4% / P1 不一致 */
export function patchP11CommercialAllowances(
    p11: TaxDepreciationAllowanceScheduleVO | null | undefined,
    args: Omit<P1CommercialBuildingLinkInput, 'p11'>,
): TaxDepreciationAllowanceScheduleVO | null {
    if (!p11) return null;
    if (hasCommercialDisposalFields(p11.commercialBuilding)
        || hasCommercialDisposalFields(p11.thisYearCommercialBuilding)) {
        return p11;
    }

    const saved = normalizePropertyList(args.properties);
    const oneReducingBf = Number(args.oneReducingBf) || 0;
    const wdvBf = Number(args.wdvBf) || 0;
    const commercialAa = annualAllowanceForBuilding(
        p11.commercialBuilding,
        saved,
        oneReducingBf,
        false,
        args.commercialFullyUsedPriorYears,
        args.commercialPriorValue,
    );
    const leaseholdAa = annualAllowanceForBuilding(
        p11.thisYearCommercialBuilding,
        saved,
        wdvBf,
        true,
        args.leaseholdFullyUsedPriorYears,
        args.leaseholdPriorValue,
    );

    const prevCommercialAa = Number(p11.commercialBuilding?.annualAllowance) || 0;
    const prevLeaseholdAa = Number(p11.thisYearCommercialBuilding?.annualAllowance) || 0;
    const prevReducingBf = Number(p11.commercialBuilding?.reducingValueBf) || 0;
    const prevWdvBf = Number(
        p11.thisYearCommercialBuilding?.wdvBf ?? p11.thisYearCommercialBuilding?.reducingValueBf,
    ) || 0;
    if (prevCommercialAa === commercialAa
        && prevLeaseholdAa === leaseholdAa
        && prevReducingBf === oneReducingBf
        && prevWdvBf === wdvBf) {
        return p11;
    }

    return {
        ...p11,
        commercialBuilding: p11.commercialBuilding
            ? {
                ...p11.commercialBuilding,
                reducingValueBf: oneReducingBf,
                annualAllowance: commercialAa,
                totalAllowance: Math.abs(commercialAa),
            }
            : p11.commercialBuilding,
        thisYearCommercialBuilding: p11.thisYearCommercialBuilding
            ? {
                ...p11.thisYearCommercialBuilding,
                wdvBf,
                reducingValueBf: wdvBf,
                annualAllowance: leaseholdAa,
                totalAllowance: Math.abs(leaseholdAa),
            }
            : p11.thisYearCommercialBuilding,
    };
}
